import { memo } from 'react';
import { Button, Group, Stack, Text } from '@mantine/core';
import { Check, Circle, Loader, Play, X, Zap } from 'lucide-react';
import { colors } from '../../theme/tokens';

/**
 * AttackPhaseTimeline — vertical stepper showing scenario phase progress.
 * Pure presentational + memoized — typically lives inside the Range detail page.
 */

export type PhaseStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface PhaseTimelineItem {
  id: string;
  name: string;
  status: PhaseStatus;
  startedAt?: string | undefined;
  completedAt?: string | undefined;
  /** Rank 4 — kill-chain detection correlation. `detected` flags whether a
   *  detection fired for this phase; `detectLatencySeconds` is start→first-
   *  detection (MTTD contribution). When `detected` is explicitly false the
   *  phase is rendered as a coverage gap. */
  detected?: boolean | undefined;
  detectLatencySeconds?: number | null | undefined;
}

interface AttackPhaseTimelineProps {
  phases: ReadonlyArray<PhaseTimelineItem>;
  /** Instructor attack trigger. When provided, each phase shows a Start button
   *  plus a "Run full chain" action. */
  onStart?: ((phaseId: string) => void) | undefined;
  /** Phase id currently dispatching (shows a loading state on its button). */
  busyPhaseId?: string | undefined;
}

const STATUS_ICON: Record<PhaseStatus, { color: string; icon: JSX.Element }> = {
  pending: { color: '#52525b', icon: <Circle size={14} /> },
  running: { color: colors.hudAmber, icon: <Loader size={14} /> },
  completed: { color: colors.neon, icon: <Check size={14} /> },
  failed: { color: colors.hudRed, icon: <X size={14} /> },
};

function AttackPhaseTimelineInner({ phases, onStart, busyPhaseId }: AttackPhaseTimelineProps) {
  return (
    <Stack gap="sm">
      {onStart ? (
        <Button
          size="xs"
          variant="light"
          color="red"
          leftSection={<Zap size={14} />}
          loading={busyPhaseId === 'all'}
          onClick={() => onStart('all')}
          style={{ alignSelf: 'flex-start' }}
        >
          Run full attack chain
        </Button>
      ) : null}
      <Stack gap={0}>
      {phases.map((phase, idx) => {
        const meta = STATUS_ICON[phase.status];
        const isLast = idx === phases.length - 1;
        return (
          <Group key={phase.id} gap="md" wrap="nowrap" align="stretch">
            <Stack gap={0} align="center" style={{ minWidth: 28 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  border: `1px solid ${meta.color}`,
                  backgroundColor: `${meta.color}1a`,
                  color: meta.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: phase.status === 'running' ? 'forge-spin 1.4s linear infinite' : 'none',
                }}
              >
                {meta.icon}
              </div>
              {isLast ? null : (
                <div
                  style={{ flex: 1, width: 1, backgroundColor: '#2a2a2d', marginTop: 2 }}
                />
              )}
            </Stack>
            <Stack gap={4} pb={isLast ? 0 : 'md'} style={{ flex: 1 }}>
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                  {phase.name}
                </Text>
                <Group gap="xs" wrap="nowrap">
                  <Text size="xs" c="dimmed" tt="uppercase">
                    {phase.status}
                  </Text>
                  {onStart ? (
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="red"
                      leftSection={<Play size={12} />}
                      loading={busyPhaseId === phase.id}
                      onClick={() => onStart(phase.id)}
                    >
                      Start
                    </Button>
                  ) : null}
                </Group>
              </Group>
              {phase.startedAt ? (
                <Text size="xs" c="dimmed" ff="JetBrains Mono">
                  start {formatTime(phase.startedAt)}
                  {phase.completedAt ? ` · end ${formatTime(phase.completedAt)}` : ''}
                </Text>
              ) : null}
              {phase.detected === true ? (
                <Text size="xs" c={colors.neon}>
                  detected
                  {typeof phase.detectLatencySeconds === 'number'
                    ? ` · MTTD ${formatLatency(phase.detectLatencySeconds)}`
                    : ''}
                </Text>
              ) : phase.detected === false && phase.startedAt ? (
                <Text size="xs" c={colors.hudRed}>
                  undetected · coverage gap
                </Text>
              ) : null}
            </Stack>
          </Group>
        );
      })}
      </Stack>
    </Stack>
  );
}

/** Human-readable detection latency (seconds → "42s" / "3m 20s" / "1h 5m"). */
export function formatLatency(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

if (typeof document !== 'undefined' && !document.getElementById('forge-spin-keyframes')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'forge-spin-keyframes';
  styleEl.textContent = `@keyframes forge-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}

export const AttackPhaseTimeline = memo(AttackPhaseTimelineInner);
